import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Hilfsfunktion zur Generierung von iCal-Dateien
const generateICS = (events, filename = "event-schedule.ics") => {
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const pad = (n) => String(n).padStart(2, "0");
    // Nutze lokale Zeit ohne Zeitzonen-Konvertierung, um Tagesverschiebungen zu vermeiden
    return (
      `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
      `T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
    );
  };

  const escapeText = (text) => {
    return text
      .replace(/\\/g, "\\\\")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,")
      .replace(/\n/g, "\\n");
  };

  let icsContent = "BEGIN:VCALENDAR\r\n";
  icsContent += "VERSION:2.0\r\n";
  icsContent += "PRODID:-//Chin Woo World Championship//Event Schedule//EN\r\n";
  icsContent += "CALSCALE:GREGORIAN\r\n";
  icsContent += "METHOD:PUBLISH\r\n";

  events.forEach((event, index) => {
    if (!event.startISO && !event.endISO) return; // Überspringe Events ohne Zeitangabe

    const startDate = formatDate(event.startISO || event.endISO);
    const endDate = formatDate(event.endISO || event.startISO);
    
    if (!startDate) return;

    // Standard-Dauer von 1 Stunde, falls kein Enddatum vorhanden
    const end = endDate || formatDate(
      new Date(new Date(event.startISO).getTime() + 60 * 60 * 1000).toISOString()
    );

    icsContent += "BEGIN:VEVENT\r\n";
    icsContent += `UID:${Date.now()}-${index}@chinwoo-worldchampionship.org\r\n`;
    icsContent += `DTSTART:${startDate}\r\n`;
    icsContent += `DTEND:${end}\r\n`;
    icsContent += `DTSTAMP:${formatDate(new Date().toISOString())}\r\n`;
    icsContent += `SUMMARY:${escapeText(event.title)}\r\n`;
    icsContent += `DESCRIPTION:${escapeText(event.description)}\r\n`;
    icsContent += `LOCATION:${escapeText(event.location)}\r\n`;
    icsContent += "STATUS:CONFIRMED\r\n";
    icsContent += "SEQUENCE:0\r\n";
    icsContent += "END:VEVENT\r\n";
  });

  icsContent += "END:VCALENDAR\r\n";

  // Erkenne iOS-Geräte für bessere Kompatibilität
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS) {
    // Auf iOS: Verwende data URI, der direkt die Kalender-App öffnet
    const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
    const link = document.createElement("a");
    link.href = dataUri;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    // Auf anderen Geräten: Standard-Download
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }
};

// Extrahierte Timeline-Item Komponente
const TimelineItem = React.memo(({ date, title, description, location, startISO, endISO, isLast, onExport, t }) => {
  const formatLocation = (loc) => {
    if (loc === "Sparkassen-Arena") {
      return (
        <>
          <img
            src={process.env.PUBLIC_URL + "/images/sponsors/sparkasse.png"}
            alt="Sparkassen Logo"
            className="h-5 w-auto inline-block align-text-bottom mr-0.5 filter drop-shadow-sm"
          />
          <span>-Arena</span>
        </>
      );
    }
    return loc;
  };

  return (
    <div className="relative pl-10 md:pl-12 pb-10 last:pb-0">
      {/* Timeline Line */}
      {!isLast && (
        <div className="absolute left-6 top-8 bottom-0 w-0.5 bg-primary/30 dark:bg-primary-light/30"></div>
      )}
      
      {/* Timeline Dot */}
      <div className="absolute left-4 top-2 w-4 h-4 bg-primary dark:bg-primary-light rounded-full border-4 border-white dark:border-slate-900 shadow-lg z-10 transition-transform duration-300 hover:scale-125"></div>

      {/* Date Badge */}
      <div className="mb-3">
        <span className="inline-block px-3 py-1 text-xs font-bold text-primary dark:text-primary-light uppercase tracking-wider bg-primary/10 dark:bg-primary-light/20 rounded-full">
          {date}
        </span>
      </div>

      {/* Content Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 gap-3">
          <h3 className="text-lg md:text-xl font-semibold text-primary dark:text-primary-light flex-1 min-w-0 break-words" style={{ hyphens: 'auto', WebkitHyphens: 'auto', msHyphens: 'auto' }}>
            {title.replace('Wertungsrichtereinweisung', 'Wertungsrichter\u00ADeinweisung')}
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (startISO || endISO) {
                onExport([{ title, description, location, startISO, endISO }], `${title.replace(/\s+/g, "-")}.ics`);
              }
            }}
            disabled={!startISO && !endISO}
            className={`flex-shrink-0 w-10 h-10 rounded-full bg-primary dark:bg-primary-light text-white flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
              startISO || endISO
                ? "cursor-pointer hover:bg-primary-dark dark:hover:bg-primary hover:shadow-lg"
                : "cursor-not-allowed opacity-50"
            }`}
            title={startISO || endISO ? (t("button.export_calendar") || "Event zum Kalender hinzufügen") : (t("eventSchedule.no_time_available") || "Keine Zeitangabe verfügbar")}
          >
            <i className="fas fa-calendar-plus text-sm"></i>
          </button>
        </div>

        {/* Description */}
        <p className="text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
          {description}
        </p>

        {/* Location */}
        <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2 text-slate-600 dark:text-slate-400">
          <i className="fas fa-map-marker-alt text-primary dark:text-primary-light"></i>
          <span>{formatLocation(location)}</span>
        </div>
      </div>
    </div>
  );
});

TimelineItem.propTypes = {
  date: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  startISO: PropTypes.string,
  endISO: PropTypes.string,
  isLast: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
};

function EventSchedule() {
  const { t } = useTranslation();
  const [showAdditionalDays, setShowAdditionalDays] = useState(false);

  // Zusammenfassen der Timeline-Daten mit useMemo
  const allTimelineItems = useMemo(() => {
    const days = [1, 2, 3, 4, 5];
    return days.flatMap((day) => {
      const dayKey = `day${day}`;
      const events = [];
      let eventIndex = 1;
      let eventKey = `event${eventIndex}`;

      while (
        t(`eventSchedule.${dayKey}.${eventKey}.title`, {
          returnObjects: true,
        }) !== `eventSchedule.${dayKey}.${eventKey}.title`
      ) {
        events.push({
          date: t(`eventSchedule.${dayKey}.${eventKey}.dateDisplay`),
          title: t(`eventSchedule.${dayKey}.${eventKey}.title`),
          description: t(`eventSchedule.${dayKey}.${eventKey}.description`),
          location: t(`eventSchedule.${dayKey}.${eventKey}.location`),
          startISO: t(`eventSchedule.${dayKey}.${eventKey}.startISO`, { returnObjects: true }) !== `eventSchedule.${dayKey}.${eventKey}.startISO` 
            ? t(`eventSchedule.${dayKey}.${eventKey}.startISO`) 
            : null,
          endISO: t(`eventSchedule.${dayKey}.${eventKey}.endISO`, { returnObjects: true }) !== `eventSchedule.${dayKey}.${eventKey}.endISO`
            ? t(`eventSchedule.${dayKey}.${eventKey}.endISO`)
            : null,
        });
        eventIndex++;
        eventKey = `event${eventIndex}`;
      }
      return events;
    });
  }, [t]);

  const mainItems = useMemo(
    () => allTimelineItems.slice(0, 3),
    [allTimelineItems]
  );
  const additionalItems = useMemo(
    () => allTimelineItems.slice(3),
    [allTimelineItems]
  );

  const allDisplayedItems = showAdditionalDays 
    ? allTimelineItems 
    : mainItems;

  const handleExportAll = () => {
    const eventsWithDates = allTimelineItems.filter(event => event.startISO || event.endISO);
    if (eventsWithDates.length > 0) {
      generateICS(eventsWithDates, "chinwoo-worldchampionship-schedule.ics");
    }
  };

  const handleExportSingle = (events, filename) => {
    generateICS(events, filename);
  };

  return (
    <section className="pt-12 pb-12 bg-slate-50/50 dark:bg-slate-900 transition-colors relative z-10" id="schedule">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
              {t("eventSchedule.title")}
              {/* Underline decoration */}
              <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
              {t("eventSchedule.description")}
            </p>
          </div>
          {/* Export All Button */}
          {allTimelineItems.filter(event => event.startISO || event.endISO).length > 0 && (
            <div className="flex justify-center mb-6">
              <button
                onClick={handleExportAll}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 shadow-sm hover:shadow-md"
              >
                <i className="fas fa-calendar-alt"></i>
                <span className="hidden sm:inline">{t("button.export_all_events") || "Alle Events zum Kalender hinzufügen"}</span>
                <span className="sm:hidden">{t("button.export_calendar") || "Kalender"}</span>
              </button>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="relative">
          {allDisplayedItems.map((item, index) => (
            <TimelineItem 
              key={`item-${index}`} 
              {...item} 
              isLast={index === allDisplayedItems.length - 1}
              onExport={handleExportSingle}
              t={t}
            />
          ))}
        </div>

        {/* Show More/Less Button */}
        {allTimelineItems.length > 3 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowAdditionalDays((prev) => !prev)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 shadow-sm hover:shadow-md"
            >
              {showAdditionalDays ? (
                <>
                  {t("button.view_less")}
                  <i className="fas fa-chevron-up"></i>
                </>
              ) : (
                <>
                  {t("button.view_more")}
                  <i className="fas fa-chevron-down"></i>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default React.memo(EventSchedule);
